import { StrictMode } from 'react'
import type { AxiosError } from 'axios'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import App from './App.tsx'
import './index.css'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { initAudio } from '@/lib/sounds'
import { isCustomDomainHost } from '@/lib/tenantHost'
import { CustomDomainGate } from '@/components/common/CustomDomainGate'

initAudio()

/**
 * Never retry a request that failed because the API is unreachable or the
 * session is gone: the outcome will not change on a second attempt, and with
 * many mounted queries the retries compound into a request storm. Retry only
 * transient server-side failures, once.
 */
function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false
  const response = (error as AxiosError | undefined)?.response
  // No response => connection refused / network down. Fail fast.
  if (!response) return false
  if (response.status === 401 || response.status === 403) return false
  if (response.status >= 400 && response.status < 500) return false
  return true
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60 * 1000, retry: shouldRetryQuery },
    mutations: { retry: 0 },
  },
})

const app = (
  <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
    <ReactQueryDevtools initialIsOpen={false} />
  </QueryClientProvider>
)

// On a customer's own domain nothing renders until the API says which
// workspace it serves (or that it serves none, or can't be reached).
createRoot(document.getElementById('root')!).render(
  <StrictMode>{isCustomDomainHost() ? <CustomDomainGate>{app}</CustomDomainGate> : app}</StrictMode>
)
